import React, { memo } from 'react'
import { Argument as ClassName } from 'classnames'
import { Controller, useForm } from 'react-hook-form'

import { isAdminDomain } from '../../../../lib/currentDomain'
import Text from '../../../../ui/Text'
import SearchInput from '../../../../admin/components/SearchInput'
import { MeasurementDevicesGetFilteredApiArg } from '../../../redux/api/web-api'
import useText from '../../../hooks/useText'
import { getProductName } from '../../../services/getProductName'

export type MeasurementDeviceSearchListFormProps = {
    className?: ClassName
    clearDevice: () => void
    onSubmit: (filters: MeasurementDevicesGetFilteredApiArg) => void
    style?: React.CSSProperties
    loading: boolean
}

export const MeasurementDeviceSearchListForm = memo(function MeasurementDeviceSearchListForm({
    clearDevice,
    onSubmit,
    loading,
    style,
}: MeasurementDeviceSearchListFormProps): React.ReactElement {
    const text = useText()
    const { register, handleSubmit, reset, control } = useForm<MeasurementDevicesGetFilteredApiArg>()

    const statuses = [
        {
            value: '',
            label: text('lblAnyStatusOption'),
        },
        {
            value: text('lblOffline'),
            label: text('lblOffline'),
        },
        {
            value: text('lblOnline'),
            label: text('lblOnline'),
        },
    ]

    const measurementDevicesProducts = [
        {
            value: '',
            label: text('lblAnyProductOption'),
        },
        {
            value: 5,
            label: getProductName(5),
        },
        {
            value: 8,
            label: getProductName(8),
        },
        {
            value: 6,
            label: getProductName(6),
        },
    ]

    return (
        <div style={style}>
            <div className="row margin-bottom-small">
                <form name="form" role="form" noValidate onSubmit={handleSubmit(onSubmit)}>
                    <div>
                        <div className="form-group">
                            <SearchInput
                                placeholder={text('lblSerialNumber')}
                                data-testid="input-serial"
                                id="deviceSerialNumber"
                                key="device SerialNumber"
                                {...register('serialNumber')}
                            />
                        </div>
                        <div className="form-group">
                            <SearchInput
                                placeholder={text('lblName')}
                                data-testid="input-name"
                                id="deviceName"
                                key="device Name"
                                {...register('name')}
                            />
                        </div>
                        {!isAdminDomain && (
                            <>
                                <div className="form-group">
                                    <SearchInput
                                        placeholder={text('lblOrganisation')}
                                        data-testid="input-organisation"
                                        id="deviceCompanyName"
                                        key="device CompanyName"
                                        {...register('companyName')}
                                    />
                                </div>

                                <div className="form-group">
                                    <SearchInput
                                        placeholder={text('lblPartnerField')}
                                        data-testid="input-serial"
                                        id="devicePartner"
                                        key="device Partner"
                                        {...register('partnerName')}
                                    />
                                </div>
                            </>
                        )}

                        <div className="col-xs-4">
                            <Controller
                                name="status"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <div className="form-group">
                                        <select
                                            title="deviceStatus"
                                            name="deviceStatus"
                                            className="form-control"
                                            onChange={onChange}
                                            value={value ?? 0}
                                        >
                                            {statuses.map((status, index) => (
                                                <option value={status.value} key={index}>
                                                    {status.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            />
                        </div>

                        <div className="col-xs-4">
                            <Controller
                                name="productId"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <div className="form-group">
                                        <select
                                            title="deviceProduct"
                                            name="deviceProduct"
                                            className="form-control"
                                            onChange={onChange}
                                            value={value ?? 0}
                                        >
                                            {measurementDevicesProducts.map((product, index) => (
                                                <option value={product.value} key={index}>
                                                    {product.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                            />
                        </div>
                    </div>

                    <div className="col-xs-12 col-md-4 pull-right">
                        <div className="form-actions">
                            <div className="pull-right">
                                <button className="btn btn-ok btn-sm" type="submit">
                                    {loading && <span className="fa fa-spinner fa-pulse" data-testid="btn-search" />}
                                    <Text code="btnSearch" />
                                </button>
                                <button
                                    className="btn btn-ok btn-sm margin-2"
                                    type="button"
                                    data-testid="btn-clear"
                                    onClick={() => {
                                        clearDevice()
                                        reset()
                                    }}
                                >
                                    <Text code="btnClear" />
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    )
})
